import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import {useCustomerType} from '@salesforce/commerce-sdk-react';
import {AccountIcon, ChevronLeftIcon} from '@salesforce/retail-react-app/app/components/icons';
import {ensureArray} from '@amblique/king-living/app/utils/utils';
import Link from '@salesforce/retail-react-app/app/components/link';
import {
  Box, Button, Divider, Flex, IconButton, Stack, Text, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import withRegistration from '@salesforce/retail-react-app/app/components/with-registration';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';

import {BookmarkIcon, ChevronRightIcon2} from '@amblique/king-living/app/components/icons';
import LocaleSelector from '@amblique/king-living/app/components/locale-selector';

const IconButtonWithRegistration = withRegistration(IconButton);

const hasSubmenu = (menuItem) => {
  return menuItem?.submenuCollection?.items?.length > 0;
};

export const MegaMenuPanel = ({
  category,
  menuItems = [],
  onBackClick,
  onArrowClick,
  onMyAccountClick,
  onMyQuoteClick,
  parentCategory,
  showFunctionalSections = true,
  transform = 'translateX(0)',
  zIndex = 1,
  ...props
}) => {
  const {isRegistered} = useCustomerType();
  const {data: customer} = useCurrentCustomer();

  const handleArrowClick = (e, menuItem) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasSubmenu(menuItem) && onArrowClick) {
      onArrowClick(menuItem);
    }
  };

  const handleTitleClick = (e, menuItem) => {
    // Only navigate if item has a link and no submenu
    if (!hasSubmenu(menuItem) && menuItem?.link) {
      // Let the link handle navigation
      return;
    }
    // Prevent navigation if it has submenu (arrow handles it)
    if (hasSubmenu(menuItem)) {
      e.preventDefault();
    }
  };

  const styles = useMultiStyleConfig('MegaMenu', {});

  return (
    <Box
      className="mega-menu-panel__mobile"
      {...styles.mobileSubcategoryPanel}
      transform={transform}
      zIndex={zIndex}
      {...props}
      sx={{
        willChange: 'transform',
        ...props.sx,
      }}
    >
      <Box className="mega-menu-panel-content" {...styles.mobileSubcategoryPanelContent}>
        {onBackClick && (
          <Flex alignItems="center" mb={4}>
            <Button
              leftIcon={<ChevronLeftIcon />}
              variant="ghost"
              onClick={onBackClick}
            >
              {parentCategory?.title || (
                <FormattedMessage defaultMessage="Back" id="mega_menu.back_button" />
              )}
            </Button>
          </Flex>
        )}
        {category && category.link && (
          <Box mb={4}>
            <Link
              target={category.link.openInNewTab ? '_blank' : undefined}
              to={category.link.url || '#'}
            >
              <Text fontSize="sm" fontWeight="semibold">
                <FormattedMessage defaultMessage="Shop All" id="mega_menu.shop_all_button" /> {category.title}
              </Text>
            </Link>
          </Box>
        )}
        <Stack className="mega-menu-items-list mega-menu-items-list__mobile" spacing={2} {...styles.mobilePanelItemsList}>
          {ensureArray(menuItems).map((item) => {
            const hasSubmenuItems = hasSubmenu(item);
            const itemLink = item?.link;

            return (
              <Box key={item.sys?.id || item.title}>
                {hasSubmenuItems ? (
                  <Flex
                    as={itemLink ? Link : 'div'}
                    target={itemLink?.openInNewTab ? '_blank' : undefined}
                    to={itemLink?.url}
                    onClick={(e) => handleTitleClick(e, item)}
                    {...styles.mobileMenuItemParentWrapper}
                  >
                    <Text {...styles.menuItemText}>
                      {item.title}
                    </Text>
                    <Box
                      as="button"
                      onClick={(e) => handleArrowClick(e, item)}
                      {...styles.menuItemIconButton}
                    >
                      <ChevronRightIcon2 {...styles.menuItemIcon} />
                    </Box>
                  </Flex>
                ) : itemLink ? (
                  <Link
                    target={itemLink.openInNewTab ? '_blank' : undefined}
                    to={itemLink.url || '#'}
                    width="100%"
                    {...styles.mobileMenuItemParentWrapper}
                  >
                    <Text {...styles.menuItemText}>
                      {item.title}
                    </Text>
                  </Link>
                ) : (
                  <Text {...styles.menuItemText}>
                    {item.title}
                  </Text>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
      {showFunctionalSections && (
        <Box
          {...styles.mobileDrawerFooter}
          className="drawer-menu-footer"
        >
          <Stack {...styles.mobileDrawerFooterStack} gap="16px">
            <Box {...styles.mobileDrawerFooterRow} {...styles.mobileDrawerLocaleSelector}>
              <LocaleSelector variant="mobile" />
            </Box>
            <Box {...styles.mobileDrawerFooterRow}>
              {isRegistered ? (
                <>
                  <Box {...styles.accountNameBadge} {...styles.mobileDrawerFooterIconButton}>
                    {customer.firstName?.charAt(0).toUpperCase()}{customer.lastName?.charAt(0).toUpperCase()}
                  </Box>
                  <Text as="span" className="mobile-drawer-footer-text">
                    <FormattedMessage defaultMessage="My Account" id="header.button.assistive_msg.my_account" />
                  </Text>
                </>) : (
                <>
                  <IconButtonWithRegistration
                    aria-label="My Account"
                    icon={<AccountIcon />}
                    variant="unstyled"
                    onClick={onMyAccountClick}
                    {...styles.mobileDrawerFooterIconButton}
                    {...styles.accountNameBadge}
                    {...styles.accountNameBadgeNotLoggedIn}
                  />
                  <Text as="span" className="mobile-drawer-footer-text">
                    <FormattedMessage defaultMessage="Sign in" id="header.button.assistive_msg.sign_in" />
                  </Text>
                </>
              )}
            </Box>
            <Box {...styles.mobileDrawerFooterRow}>
              <IconButton
                aria-label="My Quote"
                icon={<BookmarkIcon />}
                variant="unstyled"
                onClick={onMyQuoteClick}
                {...styles.mobileDrawerFooterIconButton}
              />
              <Text as="span" className="mobile-drawer-footer-text">
                <FormattedMessage defaultMessage="My Quote" id="mega_menu.my_quote_button" />
              </Text>
            </Box>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

MegaMenuPanel.displayName = 'MegaMenuPanel';
MegaMenuPanel.propTypes = {
  category: PropTypes.object,
  menuItems: PropTypes.array,
  onBackClick: PropTypes.func,
  onArrowClick: PropTypes.func,
  onMyAccountClick: PropTypes.func,
  onMyQuoteClick: PropTypes.func,
  parentCategory: PropTypes.object,
  showFunctionalSections: PropTypes.bool,
  transform: PropTypes.string,
  zIndex: PropTypes.number,
};
